import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { DragHandle, useReorder } from '@/components/ui/Reorder';
import { useToast } from '@/components/ui/ToastProvider';
import { ApiError, api } from '@/lib/api';
import type { Sop, SopsResponse } from './SopsPage';

export interface DraftStep {
  key: string;
  tab: string;
  section: string;
  label: string;
  description: string;
}

export interface SopDraft {
  title: string;
  module: string;
  owner_role_key: string;
  version: string;
  review_due_on: string;
  document_url: string;
  steps: DraftStep[];
}

const localKey = () => `s-${Math.random().toString(36).slice(2, 10)}`;

export function blankDraft(): SopDraft {
  return {
    title: '',
    module: '',
    owner_role_key: '',
    version: 'v1.0',
    review_due_on: '',
    document_url: '',
    steps: [{ key: localKey(), tab: '', section: '', label: '', description: '' }],
  };
}

export function draftFrom(sop: Sop): SopDraft {
  return {
    title: sop.title,
    module: sop.module ?? '',
    owner_role_key: sop.owner_role_key ?? '',
    version: sop.version ?? 'v1.0',
    review_due_on: sop.review_due_on ?? '',
    document_url: sop.document_url ?? '',
    steps: sop.steps.map((step) => ({
      key: localKey(),
      tab: step.tab ?? '',
      section: step.section ?? '',
      label: step.label,
      description: step.description ?? '',
    })),
  };
}

/**
 * Writing a procedure, or changing one.
 *
 * The three that ship are the client's own workbooks, imported — and there was
 * no way to add a fourth, which made the register a fixed list rather than
 * where a company keeps its procedures.
 *
 * A step carries a tab and a section because that is how the imported ones are
 * shaped: a procedure of any size is read in groups, and flattening it to a
 * list of sixty lines is how nobody finds the part they came for.
 */
export function SopEditor({
  sop,
  roles,
  onClose,
}: {
  /** Null to write a new one. */
  sop: Sop | null;
  roles: { key: string; label: string }[];
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SopDraft>(() => (sop ? draftFrom(sop) : blankDraft()));

  const setSteps = (steps: DraftStep[]) => setDraft((current) => ({ ...current, steps }));
  const reorder = useReorder(draft.steps, setSteps);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: draft.title.trim(),
        module: draft.module.trim() || null,
        owner_role_key: draft.owner_role_key || null,
        version: draft.version.trim() || null,
        review_due_on: draft.review_due_on || null,
        document_url: draft.document_url.trim() || null,
        steps: draft.steps
          // A blank line is somebody who pressed Add and changed their mind.
          .filter((step) => step.label.trim() !== '')
          .map((step) => ({
            tab: step.tab.trim() || null,
            section: step.section.trim() || null,
            label: step.label.trim(),
            description: step.description.trim() || null,
          })),
      };

      return sop
        ? api.put<SopsResponse>(`/company/sops/${sop.id}`, body)
        : api.post<SopsResponse>('/company/sops', body);
    },
    onSuccess: (fresh) => {
      queryClient.setQueryData(['company', 'sops'], fresh);
      toast.success(sop ? `${draft.title} saved` : `${draft.title} added`);
      onClose();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'That procedure could not be saved.'),
  });

  const update = (index: number, patch: Partial<DraftStep>) =>
    setSteps(draft.steps.map((step, position) => (position === index ? { ...step, ...patch } : step)));

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={sop ? `Edit ${sop.title}` : 'New SOP'}
      description="The steps are the summary people read here. If there is a live document, link it and keep that current."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            variant="positive"
            loading={save.isPending}
            disabled={draft.title.trim() === ''}
            onClick={() => save.mutate()}
          >
            {sop ? 'Save changes' : 'Add SOP'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Title" htmlFor="sop-title" required>
            <Input
              id="sop-title"
              value={draft.title}
              placeholder="e.g. Site Establishment"
              onChange={(event) => setDraft({ ...draft, title: event.target.value })}
            />
          </Field>

          <Field label="Owned by" htmlFor="sop-owner" hint="Whose job it is to keep this current.">
            <Select
              id="sop-owner"
              value={draft.owner_role_key}
              onChange={(event) => setDraft({ ...draft, owner_role_key: event.target.value })}
            >
              <option value="">Unassigned</option>
              {roles.map((role) => (
                <option key={role.key} value={role.key}>
                  {role.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Version" htmlFor="sop-version">
            <Input
              id="sop-version"
              value={draft.version}
              onChange={(event) => setDraft({ ...draft, version: event.target.value })}
            />
          </Field>

          <Field
            label="Review due"
            htmlFor="sop-review"
            hint="What the register is for — a procedure nobody has read in years."
          >
            <Input
              id="sop-review"
              type="date"
              value={draft.review_due_on}
              onChange={(event) => setDraft({ ...draft, review_due_on: event.target.value })}
            />
          </Field>

          <Field
            label="Shows on"
            htmlFor="sop-module"
            hint="The module whose ? opens it — e.g. pre-construction. Leave blank for the register only."
          >
            <Input
              id="sop-module"
              value={draft.module}
              placeholder="pre-construction"
              onChange={(event) => setDraft({ ...draft, module: event.target.value })}
            />
          </Field>

          <Field label="Live document" htmlFor="sop-url" hint="The Drive link people keep current.">
            <Input
              id="sop-url"
              value={draft.document_url}
              placeholder="https://..."
              onChange={(event) => setDraft({ ...draft, document_url: event.target.value })}
            />
          </Field>
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-2xs font-bold uppercase tracking-[0.06em] text-muted">Steps</h3>
            <Button
              size="sm"
              variant="outline"
              leadingIcon={<Plus />}
              onClick={() => {
                const last = draft.steps.at(-1);

                setSteps([
                  ...draft.steps,
                  // Carry the group down: a new line almost always belongs with
                  // the one above it, and retyping the tab each time is the
                  // reason people stop grouping at all.
                  {
                    key: localKey(),
                    tab: last?.tab ?? '',
                    section: last?.section ?? '',
                    label: '',
                    description: '',
                  },
                ]);
              }}
            >
              Add step
            </Button>
          </div>

          <ul data-reorder-list className="mt-2 space-y-2">
            {draft.steps.map((step, index) => (
              <li
                key={step.key}
                {...reorder.rowProps(index)}
                className="flex items-start gap-2 rounded-lg border border-line p-2.5 data-[dragging]:opacity-40"
              >
                <DragHandle {...reorder.handleProps(index, step.label || `step ${index + 1}`)} className="mt-1" />

                <div className="grid min-w-0 flex-1 gap-2">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input
                      aria-label={`Tab for step ${index + 1}`}
                      placeholder="Tab (optional)"
                      value={step.tab}
                      onChange={(event) => update(index, { tab: event.target.value })}
                    />
                    <Input
                      aria-label={`Section for step ${index + 1}`}
                      placeholder="Section (optional)"
                      value={step.section}
                      onChange={(event) => update(index, { section: event.target.value })}
                    />
                  </div>
                  <Input
                    aria-label={`Step ${index + 1}`}
                    placeholder="What has to happen"
                    value={step.label}
                    onChange={(event) => update(index, { label: event.target.value })}
                  />
                  <Textarea
                    rows={2}
                    aria-label={`Detail for step ${index + 1}`}
                    placeholder="The detail, if it needs any"
                    value={step.description}
                    onChange={(event) => update(index, { description: event.target.value })}
                  />
                </div>

                <Button
                  size="icon"
                  variant="ghost"
                  className="mt-1 h-8 w-8"
                  aria-label={`Remove step ${index + 1}`}
                  leadingIcon={<Trash2 />}
                  onClick={() => setSteps(draft.steps.filter((_, position) => position !== index))}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Modal>
  );
}
